import React from 'react';
import { NumericFormat } from 'react-number-format';

import {
  Checkbox,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  fieldGroupVariants,
  inputVariants,
} from '@column/ui';

import { PAYMENT_RAIL_OPTIONS, parsePaymentRails, railsToString } from '../_util/entityOptions';

// Composite entity-form inputs built on @column/ui, for controls that bundle real behavior beyond a
// bare Input/Select (a yes/no radio pair, a checkbox grid, a clamped percentage field). Plain text
// and select fields use @column/ui's Input/Select directly at their call sites.

interface YesNoInputProps {
  value?: unknown;
  onChange: (value: boolean) => void;
  isDisabled?: boolean;
}

export const YesNoInput: React.FC<YesNoInputProps> = ({ value, onChange, isDisabled }) => {
  const groupId = React.useId();
  const selected = value === true ? 'yes' : value === false ? 'no' : '';
  return (
    <RadioGroup
      className={fieldGroupVariants({ variant: 'outline', columns: 2 })}
      value={selected}
      onValueChange={(next) => onChange(next === 'yes')}
      disabled={isDisabled}
    >
      <Field orientation="horizontal">
        <RadioGroupItem value="yes" id={`${groupId}-yes`} disabled={isDisabled} />
        <FieldLabel htmlFor={`${groupId}-yes`}>Yes</FieldLabel>
      </Field>
      <Field orientation="horizontal">
        <RadioGroupItem value="no" id={`${groupId}-no`} disabled={isDisabled} />
        <FieldLabel htmlFor={`${groupId}-no`}>No</FieldLabel>
      </Field>
    </RadioGroup>
  );
};

interface CheckboxInputProps {
  checked?: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  isDisabled?: boolean;
}

export const CheckboxInput: React.FC<CheckboxInputProps> = ({ checked, onChange, label, isDisabled }) => {
  const id = React.useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked === true}
        disabled={isDisabled}
        onCheckedChange={(next) => onChange(next === true)}
      />
      {label && <Label htmlFor={id}>{label}</Label>}
    </div>
  );
};

interface PaymentRailsInputProps {
  // Comma-separated string of payment-rail values, e.g. "ach,wire".
  value?: unknown;
  onChange: (value: string) => void;
  isDisabled?: boolean;
}

export const PaymentRailsInput: React.FC<PaymentRailsInputProps> = ({ value, onChange, isDisabled }) => {
  const uid = React.useId();
  const selected = parsePaymentRails(value as string | undefined);

  const toggleRail = (rail: string, checked: boolean) => {
    const next = checked ? [...selected, rail] : selected.filter((selectedRail) => selectedRail !== rail);
    onChange(railsToString(next));
  };

  return (
    <FieldGroup variant="outline" columns={2}>
      {PAYMENT_RAIL_OPTIONS.map((option) => {
        const checkboxId = `${uid}-${option.value}`;

        return (
          <Field key={option.value} orientation="horizontal">
            <Checkbox
              id={checkboxId}
              checked={selected.includes(option.value)}
              disabled={isDisabled}
              onCheckedChange={(checked) => toggleRail(option.value, checked === true)}
            />
            <FieldLabel htmlFor={checkboxId}>{option.label}</FieldLabel>
          </Field>
        );
      })}
    </FieldGroup>
  );
};

interface StringListInputProps {
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

// Edits an API string[] field as a comma-separated text field while keeping the form value a real
// string[]. A local draft preserves exactly what's typed (including trailing separators) so the
// caret never jumps; the parsed array is pushed to the form on every keystroke, and the draft is
// re-synced from the form value whenever the field isn't being edited.
export const StringListInput: React.FC<StringListInputProps> = ({ value, onChange, placeholder, isDisabled }) => {
  const [draft, setDraft] = React.useState(() => (value ?? []).join(', '));
  const editingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editingRef.current) {
      setDraft((value ?? []).join(', '));
    }
  }, [value]);

  const parse = (text: string): string[] =>
    text
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

  return (
    <Input
      className="w-full"
      value={draft}
      placeholder={placeholder}
      disabled={isDisabled}
      onFocus={() => {
        editingRef.current = true;
      }}
      onBlur={() => {
        editingRef.current = false;
        setDraft((value ?? []).join(', '));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parse(event.target.value));
      }}
    />
  );
};

interface NumberListInputProps {
  value?: number[];
  onChange: (value: number[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

// The number[] counterpart to StringListInput (e.g. person income ranges): same comma-separated
// draft behavior, but each segment is parsed to a number and non-numeric segments are dropped.
export const NumberListInput: React.FC<NumberListInputProps> = ({ value, onChange, placeholder, isDisabled }) => {
  const [draft, setDraft] = React.useState(() => (value ?? []).join(', '));
  const editingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editingRef.current) {
      setDraft((value ?? []).join(', '));
    }
  }, [value]);

  const parse = (text: string): number[] =>
    text
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));

  return (
    <Input
      className="w-full"
      value={draft}
      placeholder={placeholder}
      disabled={isDisabled}
      inputMode="numeric"
      onFocus={() => {
        editingRef.current = true;
      }}
      onBlur={() => {
        editingRef.current = false;
        setDraft((value ?? []).join(', '));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parse(event.target.value));
      }}
    />
  );
};

interface PercentageInputProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  isDisabled?: boolean;
  hasError?: boolean;
}

const clampPercentage = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

// A whole-number percentage input: the "%" suffix trails the digits as they're typed, entry is
// capped to 0–100, and ArrowUp/ArrowDown step the value while focused. Built on NumericFormat and
// styled with the shared inputVariants so it matches the other @column/ui inputs (NumericFormat owns
// its own <input>, mirroring AmountInput, since @column/ui's Input isn't a forwardRef component).
export const PercentageInput: React.FC<PercentageInputProps> = ({ value, onChange, isDisabled, hasError }) => (
  <NumericFormat
    className={inputVariants()}
    value={value ?? ''}
    onValueChange={(values) => onChange(values.floatValue)}
    suffix="%"
    decimalScale={0}
    allowNegative={false}
    isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
    placeholder="0%"
    inputMode="numeric"
    disabled={isDisabled}
    aria-invalid={hasError || undefined}
    onKeyDown={(event) => {
      if (isDisabled || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
        return;
      }
      event.preventDefault();
      const current = typeof value === 'number' && Number.isFinite(value) ? value : 0;
      onChange(clampPercentage(current + (event.key === 'ArrowUp' ? 1 : -1)));
    }}
  />
);
